import React, { useState } from 'react';

function SearchForm({ onSearch, loading }) {
  const [formData, setFormData] = useState({
    purchaseReason: '',
    purchaseBudget: '',
    salary: '',
    lifeStyleSummary: '',
    monthlyInstallment: '',
    apartmentType: '',
    deliveryDateRange: '',
    preferredDeveloper: '',
    location: {
      longitude: '',
      latitude: '',
      description: ''
    },
    radius_km: ''
  });
  


  // Generate natural language queryString from form fields
  const generateNaturalLanguageQuery = (data) => {
    const queryParts = [];
    
    // Start with purchase intent
    if (data.purchaseReason) {
      const reason = data.purchaseReason.toLowerCase();
      if (reason.includes('first') || reason.includes('home')) {
        queryParts.push("Looking for my first home");
      } else if (reason.includes('investment')) {
        queryParts.push("Seeking investment property");
      } else if (reason.includes('upgrade')) {
        queryParts.push("Looking to upgrade my current home");
      } else if (reason.includes('downsize')) {
        queryParts.push("Looking for a smaller property to downsize");
      } else {
        queryParts.push(`Looking for property for ${data.purchaseReason}`);
      }
    }
    
    // Add property type
    if (data.apartmentType) {
      queryParts.push(`specifically a ${data.apartmentType.toLowerCase()}`);
    }
    
    // Add location preference to queryString
    if (data.location && data.location.description && data.location.description.trim()) {
      queryParts.push(`in ${data.location.description.trim()}`);
    }
    
    // Add developer preference
    if (data.preferredDeveloper) {
      queryParts.push(`by ${data.preferredDeveloper} developer`);
    }
    
    // Add budget context
    if (data.purchaseBudget) {
      const budget = parseFloat(data.purchaseBudget);
      if (budget > 0) {
        queryParts.push(`with budget around ${budget.toLocaleString()} EGP`);
      }
    }
    
    // Add delivery timeline
    if (data.deliveryDateRange !== undefined && data.deliveryDateRange !== '') {
      const timeline = parseFloat(data.deliveryDateRange);
      if (timeline === 0) {
        queryParts.push("ready to move in immediately");
      } else if (timeline <= 1) {
        queryParts.push("ready within one year");
      } else {
        queryParts.push(`delivery in ${timeline} years`);
      }
    }
    
    // Add lifestyle and amenities preferences
    if (data.lifeStyleSummary && data.lifeStyleSummary.trim()) {
      const lifestyle = data.lifeStyleSummary.toLowerCase();
      const preferences = [];
      
      // Extract key lifestyle indicators
      if (lifestyle.includes('family') || lifestyle.includes('children') || lifestyle.includes('kids')) {
        preferences.push("family-friendly environment");
      }
      if (lifestyle.includes('gym') || lifestyle.includes('fitness')) {
        preferences.push("gym and fitness facilities");
      }
      if (lifestyle.includes('pool') || lifestyle.includes('swimming')) {
        preferences.push("swimming pool");
      }
      if (lifestyle.includes('park') || lifestyle.includes('green')) {
        preferences.push("green spaces and parks");
      }
      if (lifestyle.includes('quiet') || lifestyle.includes('peaceful')) {
        preferences.push("quiet and peaceful area");
      }
      if (lifestyle.includes('modern') || lifestyle.includes('contemporary')) {
        preferences.push("modern amenities");
      }
      if (lifestyle.includes('security') || lifestyle.includes('safe')) {
        preferences.push("high security");
      }
      if (lifestyle.includes('shopping') || lifestyle.includes('mall')) {
        preferences.push("close to shopping centers");
      }
      if (lifestyle.includes('school') || lifestyle.includes('education')) {
        preferences.push("near good schools");
      }
      if (lifestyle.includes('transport') || lifestyle.includes('metro') || lifestyle.includes('commute')) {
        preferences.push("good transportation access");
      }
      
      if (preferences.length > 0) {
        queryParts.push(`with ${preferences.join(', ')}`);
      } else {
        // If no specific keywords found, include the lifestyle summary directly
        queryParts.push(`lifestyle preferences: ${data.lifeStyleSummary.trim()}`);
      }
    }
    
    // Add payment preferences
    if (data.monthlyInstallment > 0) {
      queryParts.push(`affordable monthly installments around ${parseFloat(data.monthlyInstallment).toLocaleString()} EGP`);
    }
    
    // Join parts into natural language
    let query = queryParts.join(', ');
    
    // Clean up and make it more natural
    query = query.replace(/,\s*,/g, ','); // Remove double commas
    query = query.replace(/,\s*$/, ''); // Remove trailing comma
    
    return query || "Looking for residential property with good amenities and value";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested location object
    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.purchaseReason || !formData.purchaseBudget || !formData.salary) {
      alert('Please fill in at least the purchase reason, budget, and salary fields.');
      return;
    }

    // Convert numeric fields to numbers and ensure proper formatting
    const searchData = {
      purchaseReason: formData.purchaseReason.trim(),
      purchaseBudget: parseFloat(formData.purchaseBudget) || 0,
      salary: parseFloat(formData.salary) || 0,
      lifeStyleSummary: formData.lifeStyleSummary.trim(),
      monthlyInstallment: parseFloat(formData.monthlyInstallment) || 0,
      apartmentType: formData.apartmentType || '',
      deliveryDateRange: formData.deliveryDateRange !== '' ? parseFloat(formData.deliveryDateRange) : 0,
      preferredDeveloper: formData.preferredDeveloper.trim(),
      // Format location object properly
      location: (formData.location.longitude && formData.location.latitude) ? {
        longitude: parseFloat(formData.location.longitude),
        latitude: parseFloat(formData.location.latitude),
        description: formData.location.description.trim()
      } : null,
      radius_km: formData.radius_km ? parseFloat(formData.radius_km) : null
    };

    // Generate natural language queryString using the original form data (includes location text)
    searchData.queryString = generateNaturalLanguageQuery(formData);

    // Debug log to help identify issues
    console.log('🔍 FRONTEND REQUEST DATA:');
    console.log('📊 Full Request Object:', searchData);
    console.log('💰 Budget & Salary:', { 
      purchaseBudget: searchData.purchaseBudget, 
      salary: searchData.salary 
    });
    console.log('📍 Location Data:', {
      location: searchData.location,
      radius_km: searchData.radius_km
    });
    console.log('🏠 Property Preferences:', {
      apartmentType: searchData.apartmentType,
      preferredDeveloper: searchData.preferredDeveloper,
      deliveryDateRange: searchData.deliveryDateRange
    });
    console.log('📝 Generated Query String:', searchData.queryString);

    // Show confirmation with key data being sent
    const isGeospatialSearch = searchData.location && searchData.radius_km;
    const searchType = isGeospatialSearch ? '🌍 Geospatial Search' : '🔍 Text Search';
    const locationInfo = isGeospatialSearch 
      ? `Coordinates: (${searchData.location.longitude}, ${searchData.location.latitude}) within ${searchData.radius_km}km`
      : `Location: ${searchData.location?.description || 'No specific location'}`;
    
    const confirmMessage = `${searchType}
Budget: ${searchData.purchaseBudget?.toLocaleString()} EGP
Salary: ${searchData.salary?.toLocaleString()} EGP
${locationInfo}
Apartment Type: ${searchData.apartmentType || 'Any'}
Developer: ${searchData.preferredDeveloper || 'Any'}

Generated Query: ${searchData.queryString}

Click OK to proceed with search.`;

    if (window.confirm(confirmMessage)) {
      onSearch(searchData);
    }
  };

  const handleClear = () => {
    setFormData({
      purchaseReason: '',
      purchaseBudget: '',
      salary: '',
      lifeStyleSummary: '',
      monthlyInstallment: '',
      apartmentType: '',
      deliveryDateRange: '',
      preferredDeveloper: '',
      location: {
        longitude: '',
        latitude: '',
        description: ''
      },
      radius_km: ''
    });
  };

  const apartmentTypes = [
    'Studio',
    '1 Bedroom',
    '2 Bedroom',
    '3 Bedroom',
    '4+ Bedroom',
    'Penthouse',
    'Villa',
    'Townhouse'
  ];

  const deliveryOptions = [
    { value: 0, label: 'Ready to Move' },
    { value: 1, label: 'Within 1 Year' },
    { value: 2, label: 'Within 2 Years' },
    { value: 3, label: 'Within 3 Years' },
    { value: 5, label: '5+ Years' }
  ];

  return (
    <div className="search-form-container">
      <h3>🏠 Enhanced Property Search</h3>
      <p>Fill out your preferences to get personalized property recommendations</p>
      
      <form onSubmit={handleSubmit} className="enhanced-search-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="purchaseReason">
              <span className="required">*</span> Purchase Reason
            </label>
            <textarea
              id="purchaseReason"
              name="purchaseReason"
              value={formData.purchaseReason}
              onChange={handleInputChange}
              placeholder="e.g., First home, investment property, family upgrade..."
              rows="3"
              disabled={loading}
              required
            />
          </div>
        </div>

        {/* Location and Geospatial Search */}
        <div className="form-section geospatial-form-section">
          <h4>📍 Location & Geospatial Search (Optional)</h4>
          <p className="section-description">
            <strong>For location-based search:</strong> Provide coordinates (longitude, latitude) and search radius. 
            <br />For text-based search only: Just fill in location description.
          </p>
          
          <div className="form-row">
            <div className="form-group location-description">
              <label htmlFor="location.description">
                Location Description
              </label>
              <input
                type="text"
                id="location.description"
                name="location.description"
                value={formData.location.description}
                onChange={handleInputChange}
                placeholder="e.g., New Cairo, Dubai Marina, Downtown, Zayed..."
                disabled={loading}
              />
              <small className="field-hint">
                This will be included in the search query text
              </small>
            </div>
          </div>

          <div className="coordinate-inputs">
            <div className="form-group">
              <label htmlFor="location.longitude">
                🌐 Longitude <span className="optional-badge">Optional</span>
              </label>
              <input
                type="number"
                id="location.longitude"
                name="location.longitude"
                value={formData.location.longitude}
                onChange={handleInputChange}
                placeholder="e.g., 31.2357 (Zayed: 30.9753)"
                step="0.000001"
                disabled={loading}
              />
              <small className="field-hint">East-West position (-180 to +180)</small>
            </div>

            <div className="form-group">
              <label htmlFor="location.latitude">
                🌐 Latitude <span className="optional-badge">Optional</span>
              </label>
              <input
                type="number"
                id="location.latitude"
                name="location.latitude"
                value={formData.location.latitude}
                onChange={handleInputChange}
                placeholder="e.g., 30.0444 (Zayed: 31.0118)"
                step="0.000001"
                disabled={loading}
              />
              <small className="field-hint">North-South position (-90 to +90)</small>
            </div>

            <div className="form-group">
              <label htmlFor="radius_km">
                📏 Search Radius (km) <span className="optional-badge">Optional</span>
              </label>
              <input
                type="number"
                id="radius_km"
                name="radius_km"
                value={formData.radius_km}
                onChange={handleInputChange}
                placeholder="e.g., 5 (default: 10)"
                min="0.1"
                step="0.1"
                disabled={loading}
              />
              <small className="field-hint">Distance from coordinates to search</small>
            </div>
          </div>

          <div className="geo-tips">
            <div className="tip-section">
              <strong>🎯 For Precise Location Search:</strong>
              <ul>
                <li>Fill in longitude, latitude, and radius</li>
                <li>Use <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">Google Maps</a> to find coordinates</li>
                <li>Right-click on location → "What's here?" to get coordinates</li>
              </ul>
            </div>
            <div className="tip-section">
              <strong>🔍 For Text-Only Search:</strong>
              <ul>
                <li>Just fill in location description</li>
                <li>Leave coordinates empty for broader search</li>
                <li>System will search by location name in property descriptions</li>
              </ul>
            </div>
            
            <div className="coordinate-examples">
              <strong>📍 Common Egypt Coordinates:</strong>
              <button 
                type="button"
                className="coord-example" 
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    location: {
                      ...prev.location,
                      longitude: '30.9753',
                      latitude: '31.0118',
                      description: 'Sheikh Zayed City'
                    },
                    radius_km: '10'
                  }));
                }}
              >
                Sheikh Zayed (30.9753, 31.0118)
              </button>
              <button 
                type="button"
                className="coord-example" 
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    location: {
                      ...prev.location,
                      longitude: '31.2357',
                      latitude: '30.0444',
                      description: 'New Cairo'
                    },
                    radius_km: '10'
                  }));
                }}
              >
                New Cairo (31.2357, 30.0444)
              </button>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="purchaseBudget">
              <span className="required">*</span> Purchase Budget (EGP)
            </label>
            <input
              type="number"
              id="purchaseBudget"
              name="purchaseBudget"
              value={formData.purchaseBudget}
              onChange={handleInputChange}
              placeholder="e.g., 1500000"
              min="0"
              step="10000"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="salary">
              <span className="required">*</span> Monthly Salary (EGP)
            </label>
            <input
              type="number"
              id="salary"
              name="salary"
              value={formData.salary}
              onChange={handleInputChange}
              placeholder="e.g., 25000"
              min="0"
              step="1000"
              disabled={loading}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="monthlyInstallment">
              Affordable Monthly Installment (EGP)
            </label>
            <input
              type="number"
              id="monthlyInstallment"
              name="monthlyInstallment"
              value={formData.monthlyInstallment}
              onChange={handleInputChange}
              placeholder="e.g., 8000"
              min="0"
              step="500"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="apartmentType">
              Apartment Type
            </label>
            <select
              id="apartmentType"
              name="apartmentType"
              value={formData.apartmentType}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="">Select apartment type</option>
              {apartmentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="deliveryDateRange">
              Delivery Date Range
            </label>
            <select
              id="deliveryDateRange"
              name="deliveryDateRange"
              value={formData.deliveryDateRange}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="">Select delivery timeframe</option>
              {deliveryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="preferredDeveloper">
              Preferred Developer
            </label>
            <input
              type="text"
              id="preferredDeveloper"
              name="preferredDeveloper"
              value={formData.preferredDeveloper}
              onChange={handleInputChange}
              placeholder="e.g., Emaar, Damac, Meraas..."
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="lifeStyleSummary">
              Lifestyle Summary
            </label>
            <textarea
              id="lifeStyleSummary"
              name="lifeStyleSummary"
              value={formData.lifeStyleSummary}
              onChange={handleInputChange}
              placeholder="Describe your lifestyle, preferences, and what you're looking for in a home..."
              rows="4"
              disabled={loading}
            />
          </div>
        </div>
        
        <div className="form-actions">
          <button
            type="button"
            onClick={handleClear}
            className="clear-button"
            disabled={loading}
          >
            Clear Form
          </button>
          
          <button
            type="submit"
            disabled={loading}
            className="search-button"
          >
            {loading ? (
              <>
                <span className="loading-spinner small"></span>
                Searching...
              </>
            ) : (
              <>
                🔍 Find Properties
              </>
            )}
          </button>
        </div>
      </form>
      
      <div className="search-tips">
        <h4>💡 Search Tips:</h4>
        <ul>
          <li><strong>Required fields:</strong> Purchase reason, budget, and salary are required for personalized recommendations</li>
          <li><strong>Budget guidance:</strong> Include down payment, fees, and ongoing costs in your budget</li>
          <li><strong>Lifestyle summary:</strong> Mention preferences like location, amenities, commute, or family needs for better AI matching</li>
          <li><strong>Monthly installment:</strong> Based on your financial comfort and loan terms</li>
          <li><strong>Smart Search:</strong> Your form inputs are automatically converted to natural language for AI-powered semantic search</li>
        </ul>
      </div>
    </div>
  );
}

export default SearchForm;